/**
 * WarningsDataTable
 *
 * Renders the warnings (suivis) DataGrid (MUI) with filters, cell renderers,
 * and action buttons. This is "case 1" from the original CollabsView renderBody switch.
 */
import { useState, useMemo } from "react";
import { DataGrid, QuickFilter, QuickFilterControl } from "@mui/x-data-grid";
import Langs, { Localize } from "../../app/lang/langs";
import { checkPermission } from "../../app/utils/helpers";
import { config } from "../../app/utils/config";
import ImageButton from "../../components/buttons/ImageButton";
import userInfo from "../../assets/images/userInfo.png";
import userInfoFocus from "../../assets/images/userInfoFocus.png";
import deleteIcon from "../../assets/images/delete.png";
import deleteIconFocus from "../../assets/images/delete-orange.png";
import styles from "./CollabsView.module.css";
import {
  renderWarningDateCell,
  renderCategoryCell,
  renderWarningStatusCell,
  renderCreatedAtCell,
  renderUpdatedAtCell,
  renderCommentsCell,
  usePinnedActionsColumn,
} from "./collabsTableUtils";

/**
 * @param {Object} props
 * @param {Object} props.apiRef - MUI useGridApiRef() ref
 * @param {Array} props.data - The warnings list to display
 * @param {string} props.scrollHeight - CSS scroll height for the table
 * @param {Function} props.onViewDetails - Called when the "view details" button is clicked
 * @param {Function} props.onDelete - Called when the delete button is clicked with the warning id
 */
export default function WarningsDataTable({
  apiRef,
  data,
  scrollHeight,
  onViewDetails,
  onDelete,
}) {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const hasEditPermission = checkPermission(
    config.scopes.warnings,
    config.permissionTypes.edit
  );

  usePinnedActionsColumn(apiRef);

  const columns = useMemo(() => [
    { field: "id", headerName: Localize("ID"), minWidth: 112, sortable: true, filterable: true },
    { field: "collab_matricule", headerName: Localize("Matricule"), minWidth: 128, sortable: true, filterable: true },
    { field: "collab_nom", headerName: Localize("Family Name"), minWidth: 128, sortable: true, filterable: true },
    { field: "collab_prenom", headerName: Localize("Name"), minWidth: 144, sortable: true, filterable: true },
    {
      field: "category", headerName: Localize("category"), minWidth: 176, sortable: true, filterable: true,
      renderCell: renderCategoryCell,
    },
    {
      field: "status", headerName: Localize("statut"), minWidth: 144, sortable: true, filterable: true,
      renderCell: renderWarningStatusCell,
    },
    { field: "team_name", headerName: Localize("Client"), minWidth: 144, sortable: true, filterable: true },
    {
      field: "warning_date", headerName: Localize("Date"), minWidth: 144,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderWarningDateCell,
    },
    {
      field: "created_at", headerName: Localize("created_at"), minWidth: 144,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderCreatedAtCell,
    },
    {
      field: "updated_at", headerName: Localize("updated_at"), minWidth: 144,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderUpdatedAtCell,
    },
    {
      field: "comments", headerName: Localize("DESCRIPTION"), minWidth: 144,
      sortable: false, filterable: false,
      renderCell: renderCommentsCell,
    },
    {
      field: "actions", headerName: "", width: 96, sortable: false, filterable: false,
      disableColumnMenu: true, disableReorder: true,
      renderCell: (params) => (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          {hasEditPermission && (
            <ImageButton
              image={userInfo}
              imageFocus={userInfoFocus}
              onClick={() => onViewDetails(params.row)}
              active={true}
              tooltip={<Langs str="VIEW_DETAILS" />}
              width={32}
            />
          )}
          {hasEditPermission && (
            <ImageButton
              image={deleteIcon}
              imageFocus={deleteIconFocus}
              onClick={() => onDelete(params.row.id)}
              active={true}
              tooltip={<Langs str="DELETE_WARNING" />}
              width={32}
            />
          )}
        </div>
      ),
    },
  ], [hasEditPermission, onViewDetails, onDelete]);

  const CustomToolbar = () => (
    <div className="p-2">
      <QuickFilter debounceMs={300}>
        <QuickFilterControl />
      </QuickFilter>
    </div>
  );

  return (
    <div className={styles.collabsCard}>
      <div style={{ height: scrollHeight, width: "100%" }}>
        <DataGrid
          apiRef={apiRef}
          rows={data}
          columns={columns}
          getRowId={(row) => row.id}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          slots={{ toolbar: CustomToolbar }}
          disableRowSelectionOnClick
          getRowHeight={() => "auto"}
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": { display: "flex", alignItems: "center", py: 1 },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e0e0e0",
              minHeight: 52,
            },
            "& .MuiTablePagination-root": {
              width: "100%",
              overflow: "hidden",
            },
            "& .MuiTablePagination-toolbar": {
              justifyContent: "flex-end",
              alignItems: "center",
              minHeight: 52,
              px: 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              margin: 0,
            },
            '& .MuiDataGrid-cell[data-field="actions"]': {
              backgroundColor: "var(--surface-card, #fff)",
            },
          }}
        />
      </div>
    </div>
  );
}
